import axios from "axios";

// Falls back to the hostname the page was loaded from (e.g. a LAN IP when
// accessing the dev server from a phone) instead of a hardcoded localhost,
// so REACT_APP_API_URL only needs to be set explicitly for prod deploys.
const API_URL =
  process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000/api`;
// Create a customized axios instance
const API = axios.create({ baseURL: API_URL });

// --- INTERCEPTOR: Automatically add Token to every request ---
API.interceptors.request.use((req) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

// --- INTERCEPTOR: Handle 401 Unauthorized (Auto Logout) ---
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Token expired or invalid
      logoutUser();
    }
    return Promise.reject(error);
  }
);

// --- AUTH SERVICES ---
export const loginUser = async (username, password) => {
  try {
    const response = await axios.post(`${API_URL}/token/`, {
      username,
      password,
    });
    // Save tokens to browser storage
    localStorage.setItem("access_token", response.data.access);
    localStorage.setItem("refresh_token", response.data.refresh);
    return response.data;
  } catch (error) {
    throw error.response?.data?.detail || "Login failed";
  }
};

export const logoutUser = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  window.location.href = "/login";
};

// --- DATA SERVICES (Updated to use the 'API' instance) ---
export const fetchParts = async (params) => {
  // 'params' will be { search: '...', brand: '...' }
  // axios automatically turns this into ?search=...&brand=...
  const response = await API.get("/parts/", { params });
  return response.data;
};
export const createPart = async (data) =>
  (await API.post("/parts/add/", data)).data;
export const bulkUploadParts = async (file, invoiceNumber = "") => {
  const formData = new FormData();
  formData.append('file', file);
  if (invoiceNumber) formData.append('invoice_number', invoiceNumber);
  return (await API.post("/parts/bulk-upload/", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })).data;
};
export const resolveBulkUploadConflicts = async (resolutions, invoiceNumber = "") =>
  (await API.post("/parts/bulk-upload/resolve/", { resolutions, invoice_number: invoiceNumber })).data;
export const cancelBulkUpload = async (createdPartIds) =>
  (await API.post("/parts/bulk-upload/cancel/", { created_part_ids: createdPartIds })).data;
export const updatePart = async (id, data) =>
  (await API.put(`/parts/${id}/update/`, data)).data;
export const deletePart = async (id) =>
  await API.delete(`/parts/${id}/delete/`);
// Interchangeable parts: linking joins/merges groups, unlinking takes the
// part out of its group. Both return { interchange_group, compatible: [...] }.
export const linkCompatiblePart = async (id, otherId) =>
  (await API.post(`/parts/${id}/compatible/link/`, { part_id: otherId })).data;
export const unlinkCompatiblePart = async (id) =>
  (await API.post(`/parts/${id}/compatible/unlink/`)).data;
export const restockPart = async (id, entries, sellPrice) =>
  (
    await API.post(`/parts/${id}/restock/`, {
      entries,
      ...(sellPrice !== undefined && sellPrice !== null && sellPrice !== ""
        ? { sell_price: sellPrice }
        : {}),
    })
  ).data;
export const fetchRestockHistory = async (id) =>
  (await API.get(`/parts/${id}/restock-history/`)).data;
export const returnRestockRecord = async (partId, recordId, data) =>
  (await API.post(`/parts/${partId}/restock/${recordId}/return/`, data)).data;
export const editRestockRecord = async (partId, recordId, data) =>
  (await API.put(`/parts/${partId}/restock/${recordId}/edit/`, data)).data;


export const fetchSuppliers = async () => (await API.get("/suppliers/")).data;
export const createSupplier = async (data) =>
  (await API.post("/suppliers/add/", data)).data;
export const updateSupplier = async (id, data) =>
  (await API.put(`/suppliers/${id}/update/`, data)).data;
export const deleteSupplier = async (id) =>
  await API.delete(`/suppliers/${id}/delete/`);

export const fetchVehicles = async () => (await API.get("/vehicles/")).data;
// Turn "Toyota Aqua, Honda Vezel 2018" into catalogue vehicles (existing ones
// reused, the rest created). Returns { vehicles, created, skipped }.
export const resolveVehicleList = async (text) =>
  (await API.post("/vehicles/resolve/", { text })).data;
export const createVehicle = async (data) =>
  (await API.post("/vehicles/add/", data)).data;
export const updateVehicle = async (id, data) =>
  (await API.put(`/vehicles/${id}/update/`, data)).data;
export const deleteVehicle = async (id) =>
  await API.delete(`/vehicles/${id}/delete/`);

export const createSale = async (data) =>
  (await API.post("/sales/create/", data)).data;
export const fetchSales = async () => (await API.get("/sales/")).data;
export const updateSale = async (id, data) =>
  (await API.patch(`/sales/${id}/update/`, data)).data;
export const cancelSale = async (id, data) =>
  (await API.post(`/sales/${id}/cancel/`, data)).data;
export const reverseSale = async (id) =>
  (await API.post(`/sales/${id}/reverse/`)).data;
export const markSaleAsPaid = async (id, amount) =>
  (await API.post(`/sales/${id}/mark-paid/`, amount != null ? { amount } : {})).data;
// Past repair/labour charges, grouped by description — powers the price-recall
// dropdown in the POS "Add Repair / Labor" modal. Pass the cart's plate to have
// that vehicle's own bills floated to the top of each suggestion.
// Past Charges: every completed bill for repairs matching all the typed words,
// grouped per repair with price stats.
export const fetchRepairChargeHistory = async (q) =>
  (await API.get("/repairs/history/", { params: { q } })).data;
export const fetchRepairSuggestions = async (vehicleNumber) =>
  (await API.get("/repairs/suggestions/", {
    params: vehicleNumber ? { vehicle_number: vehicleNumber } : {},
  })).data;

// --- REPAIR SERVICE CATALOG ---
export const fetchRepairServices = async (includeInactive = false) =>
  (await API.get("/repairs/services/", {
    params: includeInactive ? { include_inactive: 1 } : {},
  })).data;
export const createRepairService = async (data) =>
  (await API.post("/repairs/services/add/", data)).data;
export const bulkAddRepairServices = async (services) =>
  (await API.post("/repairs/services/bulk-add/", { services })).data;
export const updateRepairService = async (id, data) =>
  (await API.patch(`/repairs/services/${id}/update/`, data)).data;
export const deleteRepairService = async (id) =>
  await API.delete(`/repairs/services/${id}/delete/`);

export const fetchDashboardStats = async (period = 'all') =>
  (await API.get("/dashboard/stats/", { params: { period } })).data;
export const fetchDailyReport = async (date) =>
  (await API.get("/reports/daily/", { params: { date } })).data;
export const fetchMonthlyReport = async (month, year) =>
  (await API.get("/reports/monthly/", { params: { month, year } })).data;

export const fetchActiveCarts = async () => (await API.get("/active-carts/")).data;
export const syncActiveCarts = async (data) => (await API.post("/active-carts/sync/", data)).data;

// --- EMPLOYEE SERVICES ---
export const fetchEmployees = async () => (await API.get("/employees/")).data;
export const createEmployee = async (data) => (await API.post("/employees/add/", data)).data;
export const updateEmployee = async (id, data) => (await API.put(`/employees/${id}/update/`, data)).data;
export const deleteEmployee = async (id) => await API.delete(`/employees/${id}/delete/`);
export const fetchEmployeeAttendance = async (empId, month, year) =>
  (await API.get(`/employees/${empId}/attendance/`, { params: { month, year } })).data;

// --- ATTENDANCE SERVICES ---
export const fetchAttendanceSheet = async (date) => (await API.get("/attendance/", { params: { date } })).data;
export const markAttendanceSheet = async (data) => (await API.post("/attendance/mark/", data)).data;
export const fetchAttendanceMonth = async (month, year) =>
  (await API.get("/attendance/month/", { params: { month, year } })).data;

// --- HOLIDAY SERVICES ---
export const fetchHolidays = async () => (await API.get("/holidays/")).data;
export const createHoliday = async (data) => (await API.post("/holidays/add/", data)).data;
export const updateHoliday = async (id, data) => (await API.put(`/holidays/${id}/update/`, data)).data;
export const deleteHoliday = async (id) => await API.delete(`/holidays/${id}/delete/`);

// --- PAYROLL SERVICES ---
export const fetchPayroll = async (month, year) => (await API.get("/payroll/", { params: { month, year } })).data;
export const generatePayrollDrafts = async (data) => (await API.post("/payroll/generate/", data)).data;
export const updatePayroll = async (id, data) => (await API.put(`/payroll/${id}/update/`, data)).data;
export const payPayroll = async (id) => (await API.post(`/payroll/${id}/pay/`)).data;

// --- CUSTOMER SERVICES ---
export const fetchCustomers = async (search = '') => (await API.get("/customers/", { params: search ? { search } : {} })).data;
export const createCustomer = async (data) => (await API.post("/customers/add/", data)).data;
export const updateCustomer = async (id, data) => (await API.put(`/customers/${id}/update/`, data)).data;
export const deleteCustomer = async (id) => await API.delete(`/customers/${id}/delete/`);
export const addVehicleToCustomer = async (customerId, data) =>
  (await API.post(`/customers/${customerId}/vehicles/add/`, data)).data;
export const deleteCustomerVehicle = async (vehicleId) =>
  await API.delete(`/customers/vehicles/${vehicleId}/delete/`);
export const updateCustomerVehicle = async (vehicleId, data) =>
  (await API.patch(`/customers/vehicles/${vehicleId}/update/`, data)).data;
export const fetchCustomerHistory = async (customerId) =>
  (await API.get(`/customers/${customerId}/history/`)).data;

// --- VEHICLE REGISTRY SERVICES (real registered plates — independent master data) ---
export const fetchCustomerVehicles = async (search = '') =>
  (await API.get("/vehicles/registry/", { params: search ? { search } : {} })).data;
export const createCustomerVehicle = async (data) =>
  (await API.post("/vehicles/registry/add/", data)).data;
// Registered plates starting with what's typed (spaces/dashes ignored), for the
// POS plate dropdown: [{ id, vehicle_number, make, model, owner_name }].
export const suggestVehicles = async (q) =>
  (await API.get("/vehicles/registry/suggest/", { params: { q } })).data;
export const lookupVehicle = async (vehicleNumber) =>
  (await API.get("/vehicles/registry/lookup/", { params: { vehicle_number: vehicleNumber } })).data;
export const fetchVehicleHistory = async (vehicleId) =>
  (await API.get(`/vehicles/registry/${vehicleId}/history/`)).data;
export const fetchVehicleEstimates = async (vehicleId) =>
  (await API.get(`/vehicles/registry/${vehicleId}/estimates/`)).data;

// --- PURCHASE ORDER SERVICES (parts ordered from suppliers) ---
export const fetchPurchaseOrders = async (status = '') =>
  (await API.get("/purchase-orders/", { params: status ? { status } : {} })).data;
// A part's lines on open (draft/ordered) purchase orders, one per supplier order.
export const fetchPartPurchaseOrderLines = async (partId) =>
  (await API.get(`/purchase-orders/part/${partId}/`)).data;
// Adds to the supplier's open draft (creating one if needed). Returns { purchase_order, created }.
export const addPurchaseOrderItem = async (partId, supplierId, quantity) =>
  (await API.post("/purchase-orders/add-item/", { part_id: partId, supplier_id: supplierId, quantity })).data;
export const updatePurchaseOrder = async (id, data) =>
  (await API.patch(`/purchase-orders/${id}/update/`, data)).data;
export const deletePurchaseOrderItem = async (itemId) =>
  (await API.delete(`/purchase-orders/items/${itemId}/delete/`)).data;
// Reopen an ordered PO as a draft; any newer draft for the supplier is merged in.
// Returns { purchase_order, merged_from: ["PO-0006", ...] }.
export const sendPurchaseOrderToDraft = async (id) =>
  (await API.post(`/purchase-orders/${id}/to-draft/`)).data;
export const markPurchaseOrderOrdered = async (id) =>
  (await API.post(`/purchase-orders/${id}/order/`)).data;
export const receivePurchaseOrder = async (id, data) =>
  (await API.post(`/purchase-orders/${id}/receive/`, data)).data;
// Undo a receipt: takes the received stock back out and returns the order to ORDERED.
export const revertPurchaseOrder = async (id) =>
  (await API.post(`/purchase-orders/${id}/revert/`)).data;
export const cancelPurchaseOrder = async (id) =>
  (await API.post(`/purchase-orders/${id}/cancel/`)).data;
export const deletePurchaseOrder = async (id) => await API.delete(`/purchase-orders/${id}/delete/`);

// --- ESTIMATE SERVICES (insurance claim repair estimates) ---
export const fetchEstimates = async (search = '') =>
  (await API.get("/estimates/", { params: search ? { search } : {} })).data;
export const fetchEstimate = async (id) => (await API.get(`/estimates/${id}/`)).data;
export const createEstimate = async (data) => (await API.post("/estimates/create/", data)).data;
export const updateEstimate = async (id, data) => (await API.patch(`/estimates/${id}/update/`, data)).data;
export const deleteEstimate = async (id) => await API.delete(`/estimates/${id}/delete/`);

// --- VEHICLE INSPECTION SERVICES (inspection sheet and its printed report) ---
// The create/update calls take a FormData built by the caller and set no
// Content-Type, so axios supplies the multipart boundary itself — the same
// arrangement as createPart/updatePart. The sheet's JSON blobs travel as
// JSON strings in that same request, alongside the two photos.
export const fetchInspections = async (search = '') =>
  (await API.get("/inspections/", { params: search ? { search } : {} })).data;
export const fetchInspection = async (id) => (await API.get(`/inspections/${id}/`)).data;
export const createInspection = async (data) => (await API.post("/inspections/create/", data)).data;
export const updateInspection = async (id, data) => (await API.patch(`/inspections/${id}/update/`, data)).data;
export const deleteInspection = async (id) => await API.delete(`/inspections/${id}/delete/`);
export const fetchVehicleInspections = async (vehicleId) =>
  (await API.get(`/vehicles/registry/${vehicleId}/inspections/`)).data;

export default API;
